import React, { useState, useEffect, EventHandler } from "react"
import { ArrowRight, LoaderCircle } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import {
  Field,
  FieldLabel,
  FieldError,
} from "@/shadcn/components/ui/field"
import { Input } from "@/shadcn/components/ui/input"
import { Label } from "@/shadcn/components/ui/label"

// Standalone "Set a password" screen. Extracted from LoginSignup's `set-password`
// mode (Figma: S311lGXE6JSyTnNAXHmtR7, node 28202-820515) into its own full-width
// card so it can stand alone on a dedicated route.
//
// ── DEV HANDOFF (UI-only — no backend wiring) ────────────────────────────────
// Presentational. Before shipping, the dev wires:
//   • Inputs — uncontrolled placeholders. Add value/onChange (or react-hook-form)
//     + validation (match check → set `fieldError`).
//   • Buttons — inert (`type="button"`, no handlers). Wire the "Set password"
//     submit and the "I'll do this later" skip link.
//   • `fieldError` — mismatch/validation message; marks both inputs invalid.
//   • `loading` — puts "Set password" into a spinner+disabled state.
// Layout: the background (bg-card) spans the full container width, while the card
// itself stays a readable max-width (max-w-2xl) and is centered within it.
// Keep this file the source of truth; wire around it, don't fork it.

type SetPasswordProps = {
  className?: string
  title?: string
  subtitle?: string
  passwordHint?: string
  /** Field-level error (e.g. passwords don't match) — marks both inputs invalid. */
  closeAction: () => {}
  app: any,
  user: any,
  updateUser: (user: any) => {}
  apiErrors: any
}

export default function SetPassword({
  className,
  title = "Set a password",
  subtitle = "Create a password to secure your account.",
  passwordHint = "At least 8 characters.",
  closeAction,
  app,
  user,
  updateUser,
  apiErrors,
}: SetPasswordProps) {
  const [ password, setPassword ] = useState('');
  const [ passwordConfirmation, setPasswordConfirmation ] = useState('');
  const [ fieldError, setFieldError ] = useState('');
  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    // Guard on a real server error: this effect re-runs on every render (getApiErrors
    // returns a fresh array each call, so the dep identity always changes), and
    // unconditionally setting fieldError would wipe the on-blur client validation set
    // below. Any failure clears isLoading so the user can resubmit — nothing else resets
    // it, so the submit button would otherwise spin forever. Only a password rejection
    // marks the fields; anything else (a network error) is reported by the global toast.
    if (!apiErrors.length) {
      return;
    }
    setIsLoading(false);
    const serverError = apiErrors[0].errors?.validation?.password;
    if (serverError) {
      setFieldError(serverError);
    }
  }, [apiErrors]);

  const checkPassword = (event: React.FocusEvent<HTMLInputElement>) => {
    setFieldError('');
    if (password.length < 8) {
      setFieldError("Please set a password at least 8 characters long.");
    }
  };

  const checkPasswordConfirmation = (event: React.FocusEvent<HTMLInputElement>) => {
    setFieldError('');
    if (password !== passwordConfirmation) {
      setFieldError("Your password and confirmation don't match.");
    }

  };

  const setUserPassword = (e: any) => {
    e.preventDefault();

    if (password === passwordConfirmation && !fieldError) {
      let updatedUser = Object.assign({}, user);
      updatedUser.password = password;
      updateUser(updatedUser);
      setIsLoading(true);
    }
  }

  return (
    <div className="dlvr-modal full set-password" role="dialog" aria-hidden="false" aria-modal="true">
      <div className="tw-root">
        <div
          className={cn(
            "tw:flex tw:w-full tw:justify-center tw:bg-card tw:px-6 tw:py-16 tw:text-foreground",
            className
          )}
        >
          <div className="tw:flex tw:w-full tw:max-w-2xl tw:flex-col tw:rounded-lg tw:border tw:border-border tw:p-8 tw:sm:p-14">
            <Heading title={title} subtitle={subtitle} />
            <form className="tw:mt-6 tw:flex tw:flex-col tw:gap-4">
              <SetPasswordField
                label="Password"
                type="password"
                placeholder="Enter a password"
                hint={passwordHint}
                invalid={Boolean(fieldError)}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                onBlur={checkPassword}
              />
              <SetPasswordField
                label="Confirm password"
                type="password"
                placeholder="Re-enter your password"
                error={fieldError}
                value={passwordConfirmation}
                onChange={(event) => {
                  setPasswordConfirmation(event.target.value);
                }}
                onBlur={checkPasswordConfirmation}
              />
              <SubmitButton size="sm" className="tw:self-end" loading={isLoading} onClick={setUserPassword}>
                Set password
              </SubmitButton>
            </form>
            <div className="tw:mt-2 tw:flex tw:justify-center">
              <Button variant="ghost" size="sm" onClick={closeAction}>
                I’ll do this later
                <ArrowRight />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ---------- Shared pieces (self-contained; mirror LoginSignup) ---------- */

function Heading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:text-center">
      <h1 className="tw:text-3xl tw:font-semibold">{title}</h1>
      <p className="tw:text-sm tw:text-muted-foreground">{subtitle}</p>
    </div>
  )
}

function SetPasswordField({
  label,
  type,
  placeholder,
  hint,
  error,
  invalid,
  onChange,
  onBlur,
  fieldName,
  value,
}: {
  label: string
  type: string
  placeholder: string
  hint?: string
  /** Field-level error message — renders in place of the hint and marks invalid. */
  error?: string
  /** Mark the input invalid without a per-field message. */
  invalid?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  fieldName?: string
  value?: string
}) {
  const id = label.toLowerCase().replace(/\s+/g, "-")
  const isInvalid = invalid || Boolean(error)
  return (
    <Field data-invalid={isInvalid} className="tw:flex tw:w-full tw:flex-col tw:gap-2">
      <FieldLabel>{label}</FieldLabel>
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        aria-invalid={isInvalid || undefined}
        className="tw:h-8 tw:rounded-lg"
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        data-field={fieldName}
      />
      {error ? (
        <FieldError className="tw:text-xs tw:text-destructive" errors={[{
          message: error
        }]}/>
      ) : hint ? (
        <p className="tw:text-xs tw:text-muted-foreground">{hint}</p>
      ) : null}
    </Field>
  )
}

/** Primary submit button with a spinner+disabled loading state. */
function SubmitButton({
  loading,
  children,
  disabled,
  ...props
}: React.ComponentProps<typeof Button> & { loading?: boolean }) {
  return (
    <Button {...props} disabled={loading || disabled}>
      {loading ? <LoaderCircle className="tw:animate-spin" /> : null}
      {children}
    </Button>
  )
}
